import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import { ArrowForward } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import { CheckboxProps } from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import moment from "moment-timezone";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "react-international-phone/style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Logo
} from "../../assets/images";
import MuiPhone from "../../components/MuiPhone";
import PasswordStrengthIndicator from "../../components/common/PasswordStrengthIndicator";
import {
  ALL_SIGNUP_PLANS,
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  LIVE_PRICES,
  PricingAddons,
  PricingTier,
} from "../../components/subscription/pricing/pricingData";
import { config } from "../../config";
import { PLAN_NAMES } from "../../constants/planNames";
import AccountVerification from "../../modals/AccountVerification";
import AuthService from "../../services/auth.service";
import chargebeeService from "../../services/chargebee.service";
import EventsLoggerService from "../../services/events-logger.service";
import { GhlService } from "../../services/ghl";
import { LeadsquaredService } from "../../services/leadsquared/leadsquared.service";
import theme from "../../theme";
import { SocialProvider } from "../../types/auth";
import { Applications } from "../../types/enums";
import {
  sanitizeTextInput,
  validateAndSanitizeName,
  validateEmail,
} from "../../utils/common.util";
import {
  EVENT_TYPES,
  SIGNUP_FLOWS,
  SIGNUP_STEPS,
  logEvent,
} from "../../utils/events.util";
import { formatNumberWithCommas } from "../../utils/formate.utils";
import PreSignupFlow from "./PreSignupFlow";
import { loadPreSignupCompleted } from "./preSignupSession";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}


const MainSignup = () => {
  const navigate = useNavigate();

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || "UTC";
    const offset = moment.tz(timezone).format("Z");
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  const [preFlowDone, setPreFlowDone] = useState(
    () => typeof window !== "undefined" && !!loadPreSignupCompleted()
  );
  const [showPromoField, setShowPromoField] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [leadSquaredId, setLeadSquaredId] = useState<string>("");
  const [ghlContactId, setGhlContactId] = useState<string>("");

  const generateFirstName = (email: string) => {
    const emailParts = email.split("@")[0];
    const name = emailParts
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return name;
  };

  const [formData, setFormData] = useState(() => {
    const tz = getTimezoneString();
    const completed =
      typeof window !== "undefined" ? loadPreSignupCompleted() : null;
    return {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: completed?.email ?? "",
      password: "",
      timezone: tz,
      subdomain: "",
    };
  });

  // Auto-generate strong password on component mount
  // useEffect(() => {
  //   const autoGeneratedPassword = generateSecurePassword();
  //   setFormData((prev) => ({
  //     ...prev,
  //     password: autoGeneratedPassword,
  //   }));
  // }, []);
  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    subdomain: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isAutoRegistering, setIsAutoRegistering] = useState(false);
  const [isCardReady, setIsCardReady] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);

  const [verificationData, setVerificationData] = useState({
    companyId: "",
    companyName: "",
    infLeadSquaredId: "",
  });

  const [cardToken, setCardToken] = useState<string>("");
  const [cardError, setCardError] = useState<string | null>(null);
  const cardRef = useRef<any>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [planDetails, setPlanDetails] = useState<any>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [noTrial, setNoTrial] = useState(false);
  const [monthlyPlanDetails, setMonthlyPlanDetails] = useState<any>(null);
  const [initialPlanId, setInitialPlanId] = useState<string | null>(null);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discount: number;
    type: string;
  } | null>(null);
  const [promoError, setPromoError] = useState("");

  const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);

  /** Returns GHL tags for selected addons: easycrm-addons, paid-webinar-addons */
  const getAddonTagsForGhl = (): string[] => {
    const effectiveAddonIds = (calculateAllInOnePricing().addons ?? []).map((a) => a.id);
    const tags: string[] = [];
    if (effectiveAddonIds.includes("easycrm")) tags.push("easycrm-addons");
    if (effectiveAddonIds.includes("paidwebinars")) tags.push("paid-webinar-addons");
    return tags;
  };

  const planId = searchParams.get("planId");
  const cleanPlanId = planId ? planId.replace(/-\d+$/, "") : undefined;

  // Initialize billing period from URL
  const initializeBillingPeriodFromUrl = () => {
    if (cleanPlanId) {
      return cleanPlanId.includes("Yearly");
    }
    return true; // Default to yearly
  };

  const [isYearly, setIsYearly] = useState(initializeBillingPeriodFromUrl());

  // Update plan in URL when billing period changes
  const updatePlanInUrl = (newIsYearly: boolean) => {
    if (!planId) return;

    // Extract the attendee number from the current planId
    const attendeeMatch = planId.match(/-(\d+)$/);
    const attendeeNumber = attendeeMatch ? attendeeMatch[1] : "200"; // Default to 250 if no number found

    // Extract the base plan ID without period and attendee number
    const basePlanId = planId
      .replace(/(Monthly|Yearly)/g, "")
      .replace(/-\d+$/, "");
    const period = newIsYearly ? "Yearly" : "Monthly";
    const newPlanId = `${basePlanId}${period}-${attendeeNumber}`;

    setSearchParams(
      (prev) => {
        prev.set("planId", newPlanId);
        return prev;
      },
      { replace: true }
    );
  };

  const handleBillingPeriodChange = (newIsYearly: boolean) => {
    // If the user clicks on the already selected option, do nothing
    if (isYearly === newIsYearly) {
      return;
    }

    setIsYearly(newIsYearly);
    updatePlanInUrl(newIsYearly);

    // Swap the plan details without making API calls
    if (planDetails && monthlyPlanDetails) {
      if (newIsYearly) {
        // Switching to yearly: set planDetails to yearly, monthlyPlanDetails to monthly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains yearly data
        setMonthlyPlanDetails(planDetails); // planDetails contains monthly data
      } else {
        // Switching to monthly: set planDetails to monthly, monthlyPlanDetails to yearly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains monthly data
        setMonthlyPlanDetails(planDetails); // planDetails contains yearly data
      }
    }
  };

  // Set default planId if not present and track initial plan ID
  useEffect(() => {
    if (!planId) {
      // Use setSearchParams to update only the planId without reloading the page
      setSearchParams(
        (prev) => {
          prev.set("planId", "All-In-One-USD-Yearly-200");
          return prev;
        },
        { replace: true }
      );
    } else if (!initialPlanId) {
      // Set the initial plan ID only once
      setInitialPlanId(planId);
    }

    // Check if this is a free trial plan
    setIsTrial(planId === PLAN_NAMES.FREE_TRIAL);
  }, [planId, setSearchParams, initialPlanId]);

  // Store planId in localStorage when component mounts
  useEffect(() => {
    if (planId) {
      localStorage.setItem("planId", planId);
    }
  }, [planId]);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (!initialPlanId) return;

      const cleanInitialPlanId = initialPlanId.replace(/-\d+$/, "");
      setIsLoadingPlan(true);
      try {
        // Fetch both monthly and yearly plan details at once using clean plan IDs without numbers
        const basePlanId = cleanInitialPlanId.replace(/(Monthly|Yearly)/g, "");
        const monthlyPlanId = `${basePlanId}Monthly`;
        const yearlyPlanId = `${basePlanId}Yearly`;

        // Fetch both plans in parallel
        const [monthlyResponse, yearlyResponse] = await Promise.all([
          chargebeeService.getPlanById(monthlyPlanId),
          chargebeeService.getPlanById(yearlyPlanId),
        ]);

        // Store both plan details
        setPlanDetails(isYearly ? yearlyResponse.data : monthlyResponse.data);
        setMonthlyPlanDetails(
          isYearly ? monthlyResponse.data : yearlyResponse.data
        );
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
        // Fallback: try to fetch just the current plan
        try {
          const response =
            await chargebeeService.getPlanById(cleanInitialPlanId);
          setPlanDetails(response.data);
        } catch (fallbackError) {
          console.error(
            "Failed to fetch fallback plan details:",
            fallbackError
          );
        }
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPlanDetails();
  }, [initialPlanId]); // Only depend on initialPlanId, not planId

  useEffect(() => {
    // Extract plan details from planId and console log them
    if (planId) {
      // Parse the planId
      const parts = planId.split("-");
      let extractedPlanType: string;
      let currency: string;
      let duration: string;
      let extractedAttendees: number;
      let liveAttendees: number = 0;
      let automatedAttendees: number = 0;

      if (parts[0] === "All") {
        extractedPlanType = PLAN_NAMES.ALL_IN_ONE; // "All-In-One"
        currency = parts[3]; // USD
        duration = parts[4]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[5]); // e.g., 750

        // Find the matching plan to get the label
        const selectedPlan = LIVE_AUTO_PRICES.find(
          (plan) => plan.value === extractedAttendees.toString()
        );
        if (selectedPlan) {
          // Parse the label to extract live and automated attendees, handling comma-separated numbers
          const labelMatch = selectedPlan.label.match(
            /([\d,]+)\s+Live\s+&\s+([\d,]+)\s+Automated/
          );
          if (labelMatch) {
            // Remove commas and convert to numbers
            liveAttendees = parseInt(labelMatch[1].replace(/,/g, ""));
            automatedAttendees = parseInt(labelMatch[2].replace(/,/g, ""));
          }
        }
      } else {
        extractedPlanType = parts[0] + "-" + parts[1]; // Could be LIVE_WEBINARS or AUTOMATED_GOLD
        currency = parts[2]; // USD
        duration = parts[3]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[4]); // e.g., 250
      }

      // Validate that the extracted plan type is one of the valid plan names
      const validPlanTypes = [
        PLAN_NAMES.LIVE_WEBINARS,
        PLAN_NAMES.AUTOMATED_GOLD,
        PLAN_NAMES.ALL_IN_ONE,
      ];

      if (!validPlanTypes.includes(extractedPlanType)) {
        console.log(
          "Invalid plan type:",
          extractedPlanType,
          "redirecting to pricing page"
        );
        window.location.href = "https://easywebinar.com/pricing/";
        return;
      }

      // Get pricing data based on plan type
      let pricingData: PricingTier[];
      let selectedPlan: PricingTier | undefined;

      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          pricingData = LIVE_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          pricingData = AUTOMATED_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          pricingData = LIVE_AUTO_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString()
          );
          break;
        default:
          console.log("Unknown plan type:", extractedPlanType);
          return;
      }

      if (!selectedPlan) {
        console.log(
          "No matching plan found for attendees:",
          extractedAttendees
        );
        return;
      }

      // Update the features object
      const features = {
        planType: extractedPlanType,
        currency,
        duration,
        attendees: extractedAttendees ?? 0,
        liveAttendees: liveAttendees,
        automatedAttendees: automatedAttendees,
        price:
          duration === "Yearly"
            ? (selectedPlan.annual ?? 0)
            : (selectedPlan.monthly ?? 0),
        storage: selectedPlan.storage,
        webinars: "Unlimited",
      };

      // Get plan-specific features based on plan type
      let planFeatures = [];
      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited live webinars",
            "Custom landing pages + smart scheduling",
            `Up to ${selectedPlan.storage} storage space`,
            "Share offers, files & links",
            "Polls, Q&A, and emoji reactions",
          ];
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          planFeatures = [
            `Up to ${extractedAttendees} attendees`,
            "Unlimited automated events",
            "Just-in-time scheduling",
            "Preloaded + live chats & real-time replays",
            "Automated, segmented email campaigns",
            "Data export & analytics",
          ];
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          planFeatures = [
            `Up to ${extractedAttendees} total attendees`,
            "Unlimited live & automated webinars",
            "Turn replays into automated webinars",
            "Auto-played polls, CTAs, and chat prompts",
            "Priority support & success onboarding",
            "CRM Tool Integrations",
          ];
          break;
      }

      // Update all state variables at once to prevent multiple re-renders
      setPlanFeatures(features);
      setPlanType(extractedPlanType);
      setLeads(extractedAttendees);
      setLiveAttendees(liveAttendees);
      setAutomatedAttendees(automatedAttendees);
      setPlanPrice(
        duration === "Yearly"
          ? (selectedPlan.annual ?? 0)
          : (selectedPlan.monthly ?? 0)
      );
      setPlanWebinars("Unlimited");
      setPlanStorage(selectedPlan.storage);
    }
  }, [planId]);



  const handlePhoneChange = (value: string) => {
    // Only update the state, don't do any processing
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value,
    }));
  };

  const validatePassword = (password: string) => {
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (!hasMinLength) return "Password must be at least 8 characters";
    if (!hasUppercase) return "Password must contain an uppercase letter";
    if (!hasLowercase) return "Password must contain a lowercase letter";
    if (!hasNumber) return "Password must contain a number";
    if (!hasSpecialChar) return "Password must contain a special character (!@#...)";
    return "";
  };

  const validateForm = () => {
    const errors = {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      subdomain: "",
    };
    let isValid = true;

    // Check required fields with hyperlink injection protection
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = "First name is required";
      isValid = false;
    } else {
      const firstNameValidation = validateAndSanitizeName(formData.firstName, "First name");
      if (!firstNameValidation.isValid) {
        errors.firstName = firstNameValidation.error || "Invalid first name";
        isValid = false;
      } else if (formData.firstName.trim().length > 50) {
        errors.firstName = "First name must be 50 characters or less";
        isValid = false;
      }
    }

    // Last name validation with hyperlink injection protection
    if (formData.lastName && formData.lastName.trim()) {
      const lastNameValidation = validateAndSanitizeName(formData.lastName, "Last name", false);
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error || "Invalid last name";
        isValid = false;
      } else if (formData.lastName.trim().length > 50) {
        errors.lastName = "Last name must be 50 characters or less";
        isValid = false;
      }
    }

    // Email validation - check if empty first, then validate format
    if (!formData.email || !formData.email.trim()) {
      errors.email = "Work email is required";
      isValid = false;
    } else {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || "Please enter a valid email";
        isValid = false;
      }
    }

    //Phone number validation
    if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
      isValid = false;
    } else if (formData.phoneNumber.trim().length > 20) {
      errors.phoneNumber = "Phone number must be 20 characters or less";
      isValid = false;
    }

    // Password validation
    if (!formData.password.trim()) {
      errors.password = "Password is required";
      setPasswordError("Password is required");
      isValid = false;
    } else {
      const passwordValidationError = validatePassword(formData.password);
      if (passwordValidationError) {
        errors.password = passwordValidationError;
        setPasswordError(passwordValidationError);
        isValid = false;
      } else {
        setPasswordError("");
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // For subdomain field, convert to lowercase
    const processedValue = name === "subdomain" ? value.toLowerCase() : value;

    // Update the form data with the raw input value
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Validate first name with hyperlink injection protection
    if (name === "firstName") {
      if (!value.trim()) {
        setFormErrors((prev) => ({ ...prev, firstName: "First name is required" }));
      } else {
        const nameValidation = validateAndSanitizeName(value, "First name");
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({ ...prev, firstName: nameValidation.error ?? "Invalid first name" }));
        } else {
          setFormErrors((prev) => ({ ...prev, firstName: "" }));
        }
      }
    }

    // Validate last name with hyperlink injection protection
    if (name === "lastName") {
      if (value.trim()) {
        const nameValidation = validateAndSanitizeName(value, "Last name", false);
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({ ...prev, lastName: nameValidation.error ?? "Invalid last name" }));
        } else {
          setFormErrors((prev) => ({ ...prev, lastName: "" }));
        }
      } else {
        setFormErrors((prev) => ({ ...prev, lastName: "" }));
      }
    }

    // Clear error when user starts typing (for other fields)
    if (name !== "firstName" && name !== "lastName") {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "password") {
      if (!value.trim()) {
        setPasswordError("Password is required");
      } else {
        setPasswordError(validatePassword(value));
      }
    }
  };

  const [verificationDialogOpen, setVerificationDialogOpen] =
    React.useState(false);

  const handleVerificationDialogOpen = () => {
    setVerificationDialogOpen(true);
  };

  const handleVerificationDialogClose = async () => {
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? "");
    const leadData = {
      leadId: leadSquaredId,
      fields: [
        { Attribute: "mx_Plan_Name", Value: cleanPlanId },
        { Attribute: "mx_Status", Value: "Pending Verification" },
      ],
    };
    await LeadsquaredService.updateLsField(leadData);
    if (ghlContactId) {
      await GhlService.updateContactFields({
        contactId: ghlContactId,
        fields: [
          { key: "plan_name", value: fullPlanName },
          { key: "subscription_status", value: "In-Trial" },
        ],
      });
      await GhlService.addTags(ghlContactId, [fullPlanName]);
    }
    navigate("/login");
  };

  // Helper function to clear UTM params from localStorage
  const clearUtmParamsFromStorage = () => {
    localStorage.removeItem("utm_source");
    localStorage.removeItem("utm_medium");
    localStorage.removeItem("utm_campaign");
    localStorage.removeItem("utm_content");
    localStorage.removeItem("utm_term");
    localStorage.removeItem("ad_id");
    localStorage.removeItem("fbc_id");
    localStorage.removeItem("h_ad_id");
    localStorage.removeItem("utm_id");
    localStorage.removeItem("gc_id");
    localStorage.removeItem("h_ga_id");
    localStorage.removeItem("h_keyword_id");
    localStorage.removeItem("h_keyword");
    localStorage.removeItem("h_placement");
    localStorage.removeItem("g_special_campaign");
  };

  const handleVerificationDialogProceed = async () => {
    // handleVerificationDialogClose();
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? "");
    try {
      if (ghlContactId) {
        await GhlService.addTags(ghlContactId, ["email verified", fullPlanName]);
        const addonTags = getAddonTagsForGhl();
        if (addonTags.length) await GhlService.addTags(ghlContactId, addonTags);
        await GhlService.updateContactFields({
          contactId: ghlContactId,
          fields: [
            { key: "plan_name", value: fullPlanName },
            { key: "subscription_status", value: "In-Trial" },
          ],
        });
      }
      await LeadsquaredService.joinActivity({
        id: leadSquaredId,
        activityEvent: 205,
        activityValue: "Email Verified",
      });
      const leadData = {
        leadId: leadSquaredId,
        fields: [
          {
            Attribute: "mx_Plan_Name",
            Value: isTrial ? PLAN_NAMES.FREE_TRIAL : cleanPlanId,
          },
          { Attribute: "mx_Status", Value: "Active" },
        ],
      };
      await LeadsquaredService.updateLsField(leadData);
    } catch (error) {
      console.error("Error updating lead data:", error);
    }

    // Clear UTM parameters from localStorage after successful signup
    clearUtmParamsFromStorage();

    // Pass plan data through URL parameters
    const planData = {
      price: planPrice,
      currency: "USD",
      coupon: appliedPromo?.code || "",
      plan_name: planDetails?.name || cleanPlanId || "Unknown Plan",
      customer_type: "with_cc",
    };

    // Encode plan data as URL parameters
    const planDataParams = new URLSearchParams({
      ...planData,
      price: planData.price.toString(),
    }).toString();

    window.location.href = `/loading-dashboard?ew_email=${formData.email}&planId=${isTrial ? PLAN_NAMES.FREE_TRIAL : planId}&companyId=${encodeURIComponent(verificationData.companyId)}&${planDataParams}`;
  };

  const BpIcon = styled("span")(({ theme }) => ({
    borderRadius: 4,
    width: 24,
    height: 24,
    boxShadow:
      "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: "#fff",

    "input:hover ~ &": {
      border: "1px solid #666666",
    },
  }));

  const BpCheckedIcon = styled(BpIcon)({
    "&::before": {
      display: "block",
      width: 24,
      height: 24,
      backgroundImage:
        "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
        " fillRule='evenodd' clipRule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
        "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
      content: '""',
    },
    "input ~ &": {
      backgroundColor: "#178EFC",
      border: "none",
    },

    "input:hover ~ &": {
      border: "none",
    },
  });

  function BpCheckbox(props: CheckboxProps) {
    return (
      <Checkbox
        sx={{ "&:hover": { bgcolor: "transparent" } }}
        disableRipple
        color="default"
        checkedIcon={<BpCheckedIcon />}
        icon={<BpIcon />}
        {...props}
      />
    );
  }

  const handleSocialSignIn = (provider: SocialProvider) => {
    try {
      const authUrl = config.sso_auth_url;
      const planIdParam = searchParams.get("planId");

      const utmParams = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Filter out null/undefined/empty values from UTM params
      const cleanUtmParams = Object.entries(utmParams)
        .filter(
          ([_, value]) =>
            value !== null && value !== undefined && value.trim() !== ""
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value?.trim() }), {});

      // Create state parameter with additional metadata
      const stateData = {
        isRegistration: true,
        timestamp: Date.now(),
        flow: "first-flow",
        provider,
        usePreSignupFlow: true,
        ...(planIdParam && { planId: planIdParam }), // Only include planId if it exists
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }), // Only include UTM params if they exist
      };

      const baseParams = {
        client_id: config.sso_client_id,
        response_type: "code",
        scope: "email openid profile phone",
        redirect_uri: `${window.location.origin}/auth/sso/callback`,
        state: JSON.stringify(stateData),
        prompt: "select_account",
      };

      const params = new URLSearchParams({
        ...baseParams,
        identity_provider: provider,
      });

      window.location.href = `${authUrl}?${params.toString()}`;
    } catch (error) {
      console.error("Google Sign In Error:", error);
      toast.error("Failed to initialize Google Sign In");
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn("Google");
  const handleMicrosoftSignIn = () => handleSocialSignIn("Microsoft");
  const handleLinkedinSignIn = () => handleSocialSignIn("Linkedin");

  const handleGetStarted = async () => {
    let createdGhlContactId = "";
    try {
      setIsLoading(true);

      // Validation and display errors (from handleNext)
      const isValid = validateForm();
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      // Check if email exists (from handleNext)
      const emailCheckResponse = await AuthService.checkEmailExists(
        formData.email
      );
      if (emailCheckResponse.data?.exists) {
        toast.error(
          "An account with this email already exists. Please use a different email or try logging in."
        );
        setIsLoading(false);
        return;
      }

      // Use firstName and lastName from form
      const firstName = (formData.firstName || "").trim();
      const lastName = (formData.lastName || "").trim();

      // Generate subdomain from first name (use form firstName if available, otherwise fallback to email for subdomain only)
      const baseSubdomain = (firstName || generateFirstName(formData.email))
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Store the generated subdomain for use in the form
      setFormData((prev) => ({
        ...prev,
        subdomain: subdomainResponse.subdomain,
      }));

      // Log signup event (from handleNext)
      try {
        const planId = searchParams.get("planId");
        const eventData = {
          step: SIGNUP_STEPS.FORM,
          password: formData.password,
          planId: cleanPlanId || "",
          planUnits: planId ? planId.match(/-(\d+)$/)?.[1] || 10 : 10,
          signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
        };

        await logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
      } catch (error: any) {
        console.error("Error logging signup event:", error);
        // Don't block the flow if event logging fails
      }

      // Create lead in LeadSquared (from handleNext)
      try {
        let utm_Data = {
          utm_source: localStorage.getItem("utm_source"),
          utm_medium: localStorage.getItem("utm_medium"),
          utm_campaign: localStorage.getItem("utm_campaign"),
          utm_content: localStorage.getItem("utm_content"),
          utm_term: localStorage.getItem("utm_term"),
          ad_id: localStorage.getItem("ad_id"),
          fbc_id: localStorage.getItem("fbc_id"),
          h_ad_id: localStorage.getItem("h_ad_id"),
          utm_id: localStorage.getItem("utm_id"),
          gc_id: localStorage.getItem("gc_id"),
          h_ga_id: localStorage.getItem("h_ga_id"),
          h_keyword_id: localStorage.getItem("h_keyword_id"),
          h_keyword: localStorage.getItem("h_keyword"),
          h_placement: localStorage.getItem("h_placement"),
          g_special_campaign: localStorage.getItem("g_special_campaign"),
        };

        // Security: Sanitize input data before sending to LeadSquared
        const sanitizedFirstNameForLS = sanitizeTextInput(firstName);
        const sanitizedLastNameForLS = sanitizeTextInput(lastName);

        // Create form data with firstName, lastName, and phone from form
        const formDataWithDetails = {
          firstName: sanitizedFirstNameForLS, // Sanitized first name to prevent hyperlink injection
          lastName: sanitizedLastNameForLS, // Sanitized last name to prevent hyperlink injection
          email: formData.email.trim().toLowerCase(), // Normalize email
          password: formData.password,
          phone: sanitizeTextInput(formData.phoneNumber.trim()), // Sanitize phone
          timezone: getTimezoneString(),
          subdomain: subdomainResponse.subdomain,
        };

        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        await LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Signup Attempt - pricing",
        });
        const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
        if (ghlContactId) {
          createdGhlContactId = ghlContactId;
          setGhlContactId(ghlContactId);
          await GhlService.addTags(ghlContactId, ["cc trial user"]);

        }
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
      }

      // Add domain validation here
      if (!subdomainResponse.subdomain.trim()) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "Domain is required",
        }));
        setDomainSuggestions([]);
        setIsLoading(false);
        return;
      } else if (!/^[a-zA-Z0-9-]+$/.test(subdomainResponse.subdomain)) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "Domain can only contain letters, numbers, and hyphens",
        }));
        setDomainSuggestions([]);
        setIsLoading(false);
        return;
      }

      // Check domain availability first
      const domainResponse = await AuthService.checkSubDomainAvailability(
        subdomainResponse.subdomain,
        Applications.EASYWEBINAR
      );

      if (!domainResponse.available) {
        setFormErrors((prev) => ({
          ...prev,
          subdomain: "This domain is already taken. Please try another one.",
        }));
        // Set domain suggestions if they exist
        if (
          domainResponse.suggestions &&
          domainResponse.suggestions.length > 0
        ) {
          setDomainSuggestions(domainResponse.suggestions);
        } else {
          setDomainSuggestions([]);
        }
        setIsLoading(false);
        return;
      }

      // Clear suggestions if domain is available
      setDomainSuggestions([]);

      let cardTokenResult = null;
      const planId = searchParams.get("planId");

      // Only process card if there's a plan and it's not a free trial
      if (planId && !isTrial) {
        // Check if card has any validation errors
        if (cardError) {
          toast.error("Please fix card validation errors before proceeding");
          setIsLoading(false);
          return;
        }

        const result = await cardRef.current.tokenize();

        if (result.error) {
          setCardError(result.error.message || "Card tokenization failed");
          toast.error("Please check your card details.");
          setIsLoading(false);
          return;
        }

        cardTokenResult = result.token;
        setCardToken(cardTokenResult);
        setCardError(null);

        if (!cardTokenResult) {
          setCardError("Please enter valid card details");
          toast.error("Please enter valid card details");
          setIsLoading(false);
          return;
        }
      }

      // Get UTM parameters directly from URL
      const urlParams = new URLSearchParams(window.location.search);

      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      // Remove null/undefined values from the UTM params object
      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Check if buyNow parameter is present and true
      const buyNow = urlParams.get("BuyNow") == "true";

      // Security: Sanitize input data before sending to API
      // Security: Sanitize names before sending to prevent hyperlink injection
      const sanitizedFirstName = sanitizeTextInput(formData.firstName.trim());
      const sanitizedLastName = sanitizeTextInput(formData.lastName?.trim() || "");
      // const planAddons = calculateAllInOnePricing().addons ?? [];
      // const addonsPayload = isYearly ? planAddons.map((a) => ({id: a.chargeIdYearly, amount: 0})) : planAddons.map((a) => ({id: a.chargeIdmonthly, amount: 0}));
      const signupData = {
        ...formData,
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: sanitizedLastName, // Sanitized last name to prevent hyperlink injection
        email: formData.email.trim().toLowerCase(), // Normalize email
        subdomain: subdomainResponse.subdomain,
        phoneNumber: sanitizeTextInput(formData.phoneNumber.trim()), // Sanitize phone
        timezone: getTimezoneString(),
        // addOnItemPriceIds: addonsPayload,
        ...(planId &&
          !isTrial && {
          planId: planDetails.id,
          planUnits: parseInt(planId?.match(/-(\d+)$/)?.[1] || "1") || 1,
          cardToken: cardTokenResult,
          couponId: appliedPromo?.code,
        }),
        ...(isTrial && {
          planId: planDetails.id,
        }),
        // Set trialEnd to 0 if buyNow is true
        ...(buyNow && { trialEnd: 0 }),
        // Include UTM parameters if they exist
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      const response = await AuthService.signup(signupData);
      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });
      handleVerificationDialogOpen();
      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();
      toast.success("Signup successful! Please verify your account.");
      try {
        if (createdGhlContactId) {
          if (planDetails.id === "free-trial-plan") {
            await GhlService.addTags(createdGhlContactId, ["Free Plan"]);
          } else {
            await GhlService.addTags(createdGhlContactId, [isTrial ? PLAN_NAMES.FREE_TRIAL : planId ?? ""]);
          }
          const addonTags = getAddonTagsForGhl();
          if (addonTags.length) await GhlService.addTags(createdGhlContactId, addonTags);
        }
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      setDomainSuggestions([]);
      // Clear UTM params on signup failure too
      clearUtmParamsFromStorage();
      if (error.name === "INVALID_CARD") {
        setCardError("Please enter valid card details");
        toast.error("Invalid card details. Please try again.");
      } else {
        toast.error(
          error.message || "An error occurred during signup. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      toast.error("Please enter a promo code");
      return;
    }

    try {
      const planId = searchParams.get("planId");
      const response = await chargebeeService.validateCoupon(
        promoCode,
        planId ? planId.replace(/-\d+$/, "") : undefined
      );

      if (response.data.valid) {
        const { coupon } = response.data;
        setAppliedPromo({
          code: coupon.id,
          discount: coupon.discountPercentage || coupon.discountAmount,
          type: coupon.discountType,
        });
        setPromoError("");
        setShowPromoField(false);
        toast.success("Promo code applied successfully!");
      } else {
        setPromoError(response.data.error || "Invalid promo code");
        setAppliedPromo(null);
        toast.error(response.data.error || "Invalid promo code");
      }
    } catch (error: any) {
      console.error("Error validating coupon:", error);
      setPromoError("Failed to validate promo code");
      setAppliedPromo(null);
      toast.error("Failed to validate promo code. Please try again.");
    }
  };

  // Inside the Signup component, add a state variable for planFeatures
  const [planFeatures, setPlanFeatures] = useState<{
    planType: string;
    currency: string;
    duration: string;
    attendees: number;
    liveAttendees?: number;
    automatedAttendees?: number;
    price: number | string;
    webinars: number | string;
    storage: string;
  } | null>(null);

  // Add new state variables to store the extracted values
  const [planType, setPlanType] = useState<string>("");
  const [leads, setLeads] = useState<number>(0);
  const [liveAttendees, setLiveAttendees] = useState<number | undefined>(
    undefined
  );
  const [automatedAttendees, setAutomatedAttendees] = useState<
    number | undefined
  >(undefined);
  const [planPrice, setPlanPrice] = useState<number | string>("");
  const [planWebinars, setPlanWebinars] = useState<number | string>("");
  const [planStorage, setPlanStorage] = useState<string>("");

  function captureUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const email = urlParams.get("email");
    const adId = urlParams.get("ad_id");
    const fbcId = urlParams.get("fbc_id");
    const hAdId = urlParams.get("h_ad_id");
    const utmId = urlParams.get("utm_id");
    const gcId = urlParams.get("gc_id");
    const hGaId = urlParams.get("h_ga_id");
    const hKeywordId = urlParams.get("h_keyword_id");
    const hKeyword = urlParams.get("h_keyword");
    const hPlacement = urlParams.get("h_placement");
    const gSpecialCampaign = urlParams.get("g_special_campaign");

    if (utmSource) localStorage.setItem("utm_source", utmSource);
    if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
    if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
    if (utmContent) localStorage.setItem("utm_content", utmContent);
    if (utmTerm) localStorage.setItem("utm_term", utmTerm);
    if (adId) localStorage.setItem("ad_id", adId);
    if (fbcId) localStorage.setItem("fbc_id", fbcId);
    if (hAdId) localStorage.setItem("h_ad_id", hAdId);
    if (utmId) localStorage.setItem("utm_id", utmId);
    if (gcId) localStorage.setItem("gc_id", gcId);
    if (hGaId) localStorage.setItem("h_ga_id", hGaId);
    if (hKeywordId) localStorage.setItem("h_keyword_id", hKeywordId);
    if (hKeyword) localStorage.setItem("h_keyword", hKeyword);
    if (hPlacement) localStorage.setItem("h_placement", hPlacement);
    if (gSpecialCampaign) localStorage.setItem("g_special_campaign", gSpecialCampaign);
    // Set email from URL parameter if present
    if (email) {
      setFormData((prev) => ({
        ...prev,
        email: email,
      }));
    }
  }

  // Add useEffect for capturing UTM parameters
  useEffect(() => {
    captureUTMParams();
  }, []);

  /**
   * Auto-registration logic when profileId and email are present in URL parameters
   */
  useEffect(() => {
    const handleAutoRegistration = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const profileId = urlParams.get("profileId");
      const email = urlParams.get("email");

      if (profileId && email && !isLoadingPlan && planDetails) {
        try {
          setIsAutoRegistering(true);

          // Get static profile ID
          const staticProfileId = getStaticProfileId();

          // Check if the profileId matches our static ID
          if (profileId === staticProfileId) {
            // Call getLatestSignupEvent API
            const response =
              await EventsLoggerService.getLatestSignupEvent(email);

            if (response.data?.event?.event_data?.password) {
              const password = response.data.event.event_data.password;
              const planId = response.data.event.event_data.planId;
              const planUnits = 10; //response.data.event.event_data.planUnits;

              // Auto-fill the form
              setFormData((prev) => ({
                ...prev,
                email: email,
                password: password,
              }));

              // Generate firstName from email
              const firstName = generateFirstName(email);

              // Proceed with automatic registration using parsed planId and units
              await performAutoRegistration(
                email,
                password,
                firstName,
                planId,
                planUnits
              );
            } else {
              console.error("Password not found in event data");
              toast.error("Unable to retrieve registration data");
            }
          } else {
            console.log(
              "Profile ID does not match, proceeding with normal flow"
            );
            console.log("Expected:", staticProfileId, "Received:", profileId);
          }
        } catch (error: any) {
          console.error("Error during auto-registration:", error);
          toast.error(
            "Auto-registration failed. Please proceed with manual signup."
          );
        } finally {
          setIsAutoRegistering(false);
        }
      }
    };

    handleAutoRegistration();
  }, [isLoadingPlan, planDetails]);

  // Function to get static profile ID
  const getStaticProfileId = () => {
    const STATIC_PROFILE_ID = "550e8400-e29b-41d4-a716-446655440000";
    return STATIC_PROFILE_ID;
  };

  const calculateAllInOnePricing = () => {
    const defaultPlanId = "All-In-One-USD-Yearly-200";
    const currentPlanId = planId ?? defaultPlanId;
    const plan = ALL_SIGNUP_PLANS[currentPlanId] ?? ALL_SIGNUP_PLANS[defaultPlanId];

    return {
      planName: plan.planName,
      monthlyPrice: plan.monthly,
      annualPlanPerMonth: plan.annualPlanPerMonth,
      annualMaxPrice: plan.annualMaxPrice,
      liveAttendees: plan.liveAttendees,
      automatedAttendees: plan.automatedAttendees,
      savings: plan.savingsAmount,
      savingsPercentage: plan.savingsAmount ? (plan.savingsAmount / plan.annualMaxPrice) * 100 : 0,
      totalPrice: isYearly ? plan.annualPlanPerMonth * 12 : plan.monthly,
      addons: PricingAddons?.filter((addon) => plan.addons?.includes(addon.id)) ?? [],
    }
  }

  const calculateTotalPrice = () => {
    return calculateAllInOnePricing().totalPrice;
  }

  // Function to perform automatic registration
  const performAutoRegistration = async (
    email: string,
    password: string,
    firstName: string,
    planId?: string,
    units?: number
  ) => {
    let createdGhlContactId = "";
    try {
      setIsLoading(true);

      // Generate subdomain
      const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR
      );

      // Create lead in LeadSquared
      const utm_Data = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Security: Sanitize input data before sending to API
      const sanitizedFirstName = sanitizeTextInput(firstName.trim());

      const formDataWithDetails = {
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: "",
        email: email.trim().toLowerCase(), // Normalize email
        password: password,
        timezone: getTimezoneString(),
        subdomain: subdomainResponse.subdomain,
      };

      try {
        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data
        );
        const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
        if (ghlContactId) {
          createdGhlContactId = ghlContactId;
          setGhlContactId(ghlContactId);
          await GhlService.addTags(ghlContactId, ["cc trial user"]);
        }
        LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Auto Signup Attempt",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
        return;
      }

      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Prepare signup data
      const signupData = {
        ...formDataWithDetails,
        phoneNumber: "",
        ...(planId &&
          !isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data or URL
          planUnits: units || 10, // Use parsed units from event data
          couponId: appliedPromo?.code,
        }),
        ...(isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data if available
        }),
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      // Perform signup
      const response = await AuthService.signup(signupData);

      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });

      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();

      // Join activities
      try {
        if (createdGhlContactId) {
          if (planDetails?.id === "free-trial-plan") {
            await GhlService.addTags(createdGhlContactId, ["Free Plan"]);
          }
        }
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails?.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails?.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }

      logEvent(email, EVENT_TYPES.SIGNUP, {
        step: SIGNUP_STEPS.VERIFICATION,
        planId: planId,
        planUnits: units,
        autoRegistration: true,
      });

      // Open verification dialog
      handleVerificationDialogOpen();
      toast.success(
        "Auto-registration successful! Please verify your account."
      );
    } catch (error: any) {
      console.error("Auto-registration failed:", error);
      toast.error(
        error.message || "Auto-registration failed. Please try manual signup."
      );
    } finally {
      setIsLoading(false);
    }
  };



  // Check for BuyNow parameter on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const buyNow = urlParams.get("BuyNow") === "true";

    setNoTrial(buyNow);
  }, []);

  useLayoutEffect(() => {
    if (!planId) return;

    const defaultPlanId = "All-In-One-USD-Yearly-200";
    const planExists = Object.prototype.hasOwnProperty.call(ALL_SIGNUP_PLANS, planId);

    if (!planExists) {
      const url = new URL(window.location.href);
      url.searchParams.set("planId", defaultPlanId);
      window.location.replace(url.toString());
    }
  }, [planId]);

  if (!preFlowDone) {
    return (
      <PreSignupFlow
        onComplete={(email) => {
          setFormData((prev) => ({ ...prev, email }));
          setPreFlowDone(true);
        }}
        onSocialSignIn={handleSocialSignIn}
      />
    );
  }

  return (
    <>
      {/* Auto-registration loading overlay */}
      {isAutoRegistering && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
            Setting up your account...
          </Typography>
          <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
            Please wait while we complete your registration
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          // background: `
          //       linear-gradient(180deg, #010007 26.92%, #AC8DFF 100%),
          //       linear-gradient(180deg, #FFFFEF 0%, #FFF 100%)
          //   `,
          color: "#1f2937",
          fontFamily: "Inter",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: "16px 24px", sm: "24px 32px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={Logo}
              alt="logo"
              style={{ height: 24, cursor: "pointer", maxWidth: "100%" }}
              onClick={() => window.open("https://easywebinar.com/", "_blank")}
            />
          </Box>
          {formData.email ? (
            <Box
              aria-label="signup-email-initial"
              sx={{
                height: 36,
                width: 36,
                borderRadius: "999px",
                bgcolor: "rgba(96, 102, 233, 0.12)",
                color: "#6066E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "0.95rem",
                userSelect: "none",
              }}
            >
              {(formData.email || "").trim().charAt(0).toUpperCase() || "U"}
            </Box>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              size="small"
              onClick={() => navigate("/login")}
              sx={{
                bgcolor: "#6066E9",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#4B4EB5" },
              }}
            >
              Login
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: {
              xs: "calc(100vh - 120px)",
              sm: "calc(100vh - 100px)",
              md: "calc(100vh - 80px)",
            },
            p: { xs: 1, sm: 2 },
            pt: { xs: 0, sm: 2, md: 3 },
          }}
        >
          {/* Main container with border - responsive sizing */}
          <Box
            sx={{
              width: { xs: "100%", sm: "90%", md: "1200px", lg: "1400px" },
              maxWidth: { xs: "600px", md: "none" },
              height: { xs: "auto" },
              // border: "1.5px solid #e5e7eb",
              // borderRadius: "18px",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              px: { xs: 0, sm: 2, md: 3 },
              // bgcolor: "#ffffff",
              // boxShadow: "0 4px 32px 0 rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Left side - Form Section */}
            <Box
              sx={{
                width: { xs: "100%", md: "45%" },
                bgcolor: { xs: "#fff", sm: "#fafafa" },
                px: { xs: 3, sm: 4, md: 6 },
                pt: { xs: 4, sm: 4, md: 6 },
                pb: { xs: 0, sm: 4, md: 6 },
                display: "flex",
                flexDirection: "column",
                // borderLeft: { md: "1px solid #e5e7eb" },
              }}
            >
              <Box sx={{ textAlign: "center", mb: 3, display: { xs: "block", md: "none" } }}>
                <Typography
                  color="#333333"
                  fontWeight="700"
                  fontSize="20px"
                  mb="8px"
                  lineHeight="1.2"
                >
                  Start Your Full-Access Free Trial
                </Typography>
                <Box
                  sx={{
                    color: "#374151",
                    fontSize: "15px",
                    display: "inline-block",
                  }}
                >
                  After 7-Day Free Trial: {isYearly ? <Box component="span" sx={{ textDecoration: "line-through", fontSize: "16px", color: "#9ca3af" }}>
                    ${formatNumberWithCommas(calculateAllInOnePricing().annualMaxPrice)}
                  </Box> : ""} <b>${formatNumberWithCommas(isYearly ? calculateAllInOnePricing().annualPlanPerMonth : calculateTotalPrice())}/month {isYearly ? "(billed annually)" : ""}</b>, cancel anytime
                </Box>
              </Box>
              <Box mb={{ xs: 3, sm: 4 }}>
                <Typography fontSize={{ xs: "20px", sm: "24px" }} fontWeight={700} mb={{ xs: 2, sm: 3 }} color="#000">
                  {calculateAllInOnePricing().planName} Plan
                </Typography>

                <Box display="flex" alignItems="center" gap={2} mb={{ xs: 2, sm: 2.5 }} flexWrap="wrap">
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      border: "1px solid #ECECEC",
                      borderRadius: "14px",
                      overflow: "hidden",
                      p: 0.5,
                      background: "#FAFAFB",
                      width: "fit-content",
                    }}
                  >
                    <Box
                      onClick={() => handleBillingPeriodChange(true)}
                      sx={{
                        px: { xs: 2.5, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        background: isYearly ? "#161B26" : "transparent",
                        color: isYearly ? "#fff" : "#232434",
                        borderRadius: "10px",
                        fontWeight: 500,
                        fontSize: { xs: "13px", sm: "14px" },
                        cursor: "pointer",
                        boxShadow: isYearly ? "0 2px 8px 0 #181B2020" : "none",
                        border: "none",
                        transition: "background 0.18s, color 0.18s",
                        zIndex: isYearly ? 2 : 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Annual
                    </Box>
                    <Box
                      onClick={() => handleBillingPeriodChange(false)}
                      sx={{
                        px: { xs: 2.5, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        background: !isYearly ? "#161B26" : "transparent",
                        color: !isYearly ? "#fff" : "#232434",
                        borderRadius: "10px",
                        fontWeight: 500,
                        fontSize: { xs: "13px", sm: "14px" },
                        cursor: "pointer",
                        boxShadow: !isYearly ? "0 2px 8px 0 #181B2020" : "none",
                        border: "none",
                        transition: "background 0.18s, color 0.18s",
                        zIndex: !isYearly ? 2 : 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Monthly
                    </Box>
                  </Box>
                  {isYearly && (
                    <Box
                      sx={{
                        backgroundColor: "#16A34A",
                        color: "#fff",
                        fontSize: { xs: "13px", sm: "14px" },
                        fontWeight: 500,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 0.75, sm: 1 },
                        borderRadius: "20px",
                      }}
                    >
                      You save ${formatNumberWithCommas(calculateAllInOnePricing().savings)}/year
                    </Box>
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: "#131927", fontSize: "14px", mb: 3 }}>
                  7-day free trial then billed {isYearly ? "annually" : "monthly"}
                </Typography>
                <Box sx={{
                  bgcolor: "#F3F4F6",
                  borderRadius: "12px",
                  p: { xs: 2, sm: 3 },
                  mt: 4,
                }}>
                  <Box
                    sx={{
                      borderBottom: "1px solid #d4d4d4fa",
                      pb: { xs: 2, sm: 3 },
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography fontSize="16px" fontWeight={400} color="#000">
                        {calculateAllInOnePricing().planName} Plan ({isYearly ? "Annual" : "Monthly"})
                      </Typography>
                      <Typography fontSize="18px" fontWeight={600} color="#000">
                        ${formatNumberWithCommas(isYearly ? calculateAllInOnePricing().annualPlanPerMonth * 12 : calculateAllInOnePricing().monthlyPrice)}/{isYearly ? "yr" : "mo"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ pt: { xs: 2, sm: 3 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography fontSize="16px" fontWeight={400} color="#000">
                        Due today
                      </Typography>
                      <Typography fontSize={{ xs: "24px", sm: "28px" }} fontWeight={600} color="#000" sx={{ lineHeight: 1 }}>
                        $0.00
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#999", fontSize: "13px", textAlign: "right", letterSpacing: "0.05em" }}>
                      ${formatNumberWithCommas(isYearly ? calculateAllInOnePricing().annualPlanPerMonth * 12 : calculateAllInOnePricing().monthlyPrice)} billed after trial
                    </Typography>
                  </Box>

                </Box>
              </Box>


              {/* Savings Box - Show when yearly is selected */}


              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "1px solid #e5e5e5",
                  p: 2,
                  mb: 3,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography fontWeight={600} fontSize="16px" color="#333333">
                    {calculateAllInOnePricing().planName} Plan
                  </Typography>
                    <Typography fontWeight={600} fontSize="16px" color="#333333">
                      {isYearly ? `$${formatNumberWithCommas(calculateAllInOnePricing().annualPlanPerMonth * 12)}` : `$${formatNumberWithCommas(calculateAllInOnePricing().monthlyPrice)}`}
                    </Typography>
                </Box>
                  <Typography variant="body2" sx={{ color: "#666666", fontSize: "13px", mb: 0.75 }}>
                    {isYearly
                      ? `${calculateAllInOnePricing().annualPlanPerMonth}/mo x 12 months`
                      : `${calculateAllInOnePricing().monthlyPrice}/mo x 1 month`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666666", fontSize: "12px" }}>
                    {(liveAttendees ?? 0) > 0 && (automatedAttendees ?? 0) > 0
                      ? `${(liveAttendees ?? 0).toLocaleString()} Live Attendees • ${(automatedAttendees ?? 0).toLocaleString()} Automated Attendees`
                      : `${leads.toLocaleString()} Attendees`}
                  </Typography>
              </Box> */}
              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "1px solid #e5e5e5",
                  textAlign: "center",
                  p: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 1 }}>
                  {isYearly && (
                    <Typography sx={{ color: "#9ca3af", fontSize: { xs: "16px", sm: "18px" }, textDecoration: "line-through", mb: { xs: 0.25, sm: 0.5 } }}>
                      ${formatNumberWithCommas(calculateAllInOnePricing().annualMaxPrice)}
                    </Typography>
                  )}
                  <Typography fontWeight={700} fontSize="28px" color="#111">
                    ${formatNumberWithCommas(calculateAllInOnePricing()?.[isYearly ? "annualPlanPerMonth" : "monthlyPrice"])}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "14px", my: 1 }}>
                  per month, billed {isYearly ? "annually" : "monthly"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#9ca3af", fontSize: "13px" }}>
                  {calculateAllInOnePricing().liveAttendees ? `${formatNumberWithCommas(calculateAllInOnePricing().liveAttendees ?? 0)} Live Attendees • ` : ""}
                  {calculateAllInOnePricing().automatedAttendees ? `${formatNumberWithCommas(calculateAllInOnePricing().automatedAttendees ?? 0)} Automated Attendees` : ""}
                </Typography>
              </Box> */}
              {/* {isYearly && (
                <Box
                  sx={{
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #4caf50",
                    borderRadius: "8px",
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Typography fontWeight={600} sx={{ color: "#2e7d32", fontSize: "15px", mb: { xs: 1, sm: 1.5 } }}>
                    Annual Plan Bonuses
                  </Typography>
                  <Box sx={{ "& > div": { display: "flex", alignItems: "center", gap: 1, mb: 0.75 } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>✓</Typography>
                      <Typography variant="body2" sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>
                        2 months free with annual billing
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>✓</Typography>
                      <Typography variant="body2" sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>
                        EasyCRM (Complete CRM & automation)
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>✓</Typography>
                      <Typography variant="body2" sx={{ color: "#2e7d32", fontSize: { xs: "12px", sm: "14px" } }}>
                        Paid Webinars (Monetize your webinars)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )} */}
              {/* Always Included */}
              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "1px solid #e5e5e5",
                  p: 2,
                  mb: 3,
                }}
              >
                <Typography
                  fontWeight={700}
                  fontSize="12px"
                  color="#555555"
                  letterSpacing="0.05em"
                  sx={{ textTransform: "uppercase", mb: 1.5 }}
                >
                  Always Included
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography sx={{ color: "#2e7d32", fontSize: "14px", lineHeight: 1.5 }}>✓</Typography>
                  <Typography variant="body2" sx={{ color: "#666666", fontSize: "14px", flex: 1 }}>
                    AI Webinar Builder - AI builds your pages, emails, polls, and offers
                  </Typography>
                </Box>
              </Box> */}

              {/* Premium Add-ons - Interactive when monthly, static when yearly */}

              {/* {!!calculateAllInOnePricing().addons?.length &&
                <Box mb={{ xs: 1, sm: 2 }} pb={1.5}>
                  <Typography fontWeight={600} fontSize={{ xs: "14px", sm: "16px" }} color="#333333" mb={{ xs: 1.5, sm: 2 }}>
                    Available Add-ons
                  </Typography>
                  {calculateAllInOnePricing().addons?.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => toggleAddon(item.id)}
                      sx={{
                        // border: addons.some((a) => a.id === item.id) || isYearly ? "1px solid #4caf50" : "1px solid #e0e0e0",
                        borderBottom: "1px solid #C5C5C5",
                        // borderRadius: "8px",
                        // backgroundColor: addons.some((a) => a.id === item.id) || isYearly ? "#f0fdf4" : "#fff",
                        // p: { xs: 1.5, sm: 2 },
                        pb: 1.5,
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: isYearly ? "default" : "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": !isYearly ? { borderColor: "#9ca3af" } : {},
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1.5} flex={1}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            mt: 0.5,
                            borderRadius: "4px",
                            border: addons.some((a) => a.id === item.id) || isYearly ? "none" : "2px solid #000000",
                            backgroundColor: addons.some((a) => a.id === item.id) || isYearly ? "#0095FF" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {(addons.some((a) => a.id === item.id) || isYearly) && (
                            <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: 700 }}>✓</Typography>
                          )}
                        </Box>
                        <Box>
                          <Typography fontWeight={600} fontSize={{ xs: "13px", sm: "15px" }} color="#333333">
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "12px", sm: "13px" }, opacity: 0.7 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        {isYearly ? (
                          <>
                            <Typography fontWeight={600} fontSize="14px" color="secondary.main" display="block">
                              Included (${item.price.toFixed(0)}/mo value)
                            </Typography>
                            <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.25 }}>
                              Total annual savings: ${formatNumberWithCommas(Math.round(item.price * 12))}
                            </Typography>
                          </>
                        ) : (
                          <Typography fontWeight={600} fontSize="15px" color="#1A1A1A">
                            ${item.price.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              } */}

              {/* {isYearly && (() => {
                const quantity = planId ? parseInt(planId.match(/-(\d+)$/)?.[1] || "0") : 0;
                const eventQuantities = [50, 200, 500];
                const type: "events" | "funnels" = eventQuantities.includes(quantity) ? "events" : "funnels";
                const plan = ALL_IN_ONE_PRICES[type][quantity as keyof typeof ALL_IN_ONE_PRICES.events] ?? ALL_IN_ONE_PRICES.events["200"];
                const pricing = calculateAllInOnePricing();
                const baseNetPrice = (plan?.annualMaxPrice ?? 0) * 12;
                const discountAmount = pricing.savings ?? 0;
                const twoMonthsSavings = 2 * (plan?.monthly ?? 0);
                const addonsList = pricing.addons ?? [];
                return (
                );
              })()} */}
              {/* <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  // border: "1px solid #E8EAF0",
                  // boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                  p: {xs: "0", md: "24px 20px"},
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1.5, borderBottom: "1px solid #E8EAF0" }}>
                  <Typography fontSize="14px" color="#000">Base Net Price</Typography>
                  <Typography fontWeight={700} fontSize="16px" color="text.tertiary">
                    ${formatNumberWithCommas(isYearly ? (calculateAllInOnePricing().annualMaxPrice ?? 0) * 12 : calculateAllInOnePricing().monthlyPrice)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderBottom: "1px solid #E8EAF0" }}>
                  <Typography fontSize="12px" fontWeight={600} color="secondary.main" sx={{ letterSpacing: "0.02em", textTransform: "uppercase" }}>
                    DISCOUNT - ANNUAL BILLING
                  </Typography>
                  <Typography fontWeight={700} fontSize="16px" color="secondary.main">
                    {isYearly ? `-$${formatNumberWithCommas(calculateAllInOnePricing().savings ?? 0)}` : "$0"}
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.5, pt: 1.5, "& li": { mb: 0.75, fontSize: "13px", fontWeight: 600, color: "#6b7280", lineHeight: 1.5, listStyleType: "initial" } }}>
                  {isYearly && (
                    <li>2-months free (${formatNumberWithCommas(2 * (calculateAllInOnePricing().monthlyPrice ?? 0))} savings)</li>
                  )}
                  {isYearly && (
                    calculateAllInOnePricing().addons?.map((addon) => (
                      <li key={addon.id}>{addon?.title} included (${formatNumberWithCommas(addon.price * 12)} savings)</li>
                    ))
                  )}
                </Box>
              </Box> */}

              <Box
                sx={{
                  // p: { xs: "0", md: "20px" },
                  my: 2,
                }}
              >
                {/* <Typography
                  sx={{
                    fontSize: "14px",
                    lineHeight: 1,
                    letterSpacing: "0.04em",
                    color: "#9CA3AF",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    mb: 2,
                  }}
                >
                  What's Included
                </Typography> */}

                {[
                  "No platform fees — keep 100% of what you make",
                  "7-day full access, cancel anytime — no strings attached",
                  "Webinars, CRM, Al & checkout — one platform, zero duct tape",
                ].map((feature) => (
                  <Box key={feature} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        // bgcolor: "#D1FAE5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ color: "#222", fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>✓</Typography>
                    </Box>
                    <Typography sx={{ color: "#333", fontSize: { xs: "12px", md: "14px" }, fontWeight: 400, lineHeight: 1.35 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Total */}
              {/* <Box sx={{
                p: { xs: "0", md: "24px 20px" },
                mb: { xs: 0, sm: 2 }, pb: 1.5, bgcolor: "#fff",
                borderRadius: "12px",
                // border: "1px solid #E8EAF0",
              }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ color: "#000", fontSize: "16px", fontWeight: 600 }}>
                      Total after trial
                    </Typography>
                    <Typography fontSize="12px" color="#000" sx={{ opacity: 0.7, my: 0.5 }}>
                      {!isYearly ? "Includes plan + selected add-ons" : "Annual billing"}
                    </Typography>
                    {!isYearly && (
                      <Typography fontSize="12px" color="#667EEA" sx={{ cursor: "pointer" }} onClick={() => handleBillingPeriodChange(true)}>
                        Switch to annual and save ${formatNumberWithCommas(calculateAllInOnePricing().savings)} {calculateAllInOnePricing().addons?.length > 0 ? "+ unlock 2 bonus features" : ""}
                      </Typography>
                    )}
                  </Box>
                  <Typography fontWeight={600} fontSize={{ xs: "24px", md: "32px" }} color="#111">
                    ${formatNumberWithCommas(calculateTotalPrice())}
                  </Typography>
                </Box>
              </Box> */}
              {/* <Typography variant="body2" sx={{ color: "#6b7280", fontSize: { xs: "12px", md: "13px" }, mt: { xs: 1, md: 0.5 }, textAlign: "center" }}>
                Cancel anytime • No charge if canceled during trial
              </Typography> */}


              {/* <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  mt: 1,
                  borderRadius: "8px",
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={600} fontSize="16px" color="#333333">
                    Due Today
                  </Typography>
                  <Typography fontWeight={600} fontSize="22px" color="#333333" sx={{ lineHeight:1 }}>
                    $0
                  </Typography>
                </Box>
                <Typography variant="body2" color="#6b7280" mt={1} sx={{ fontSize: "12px" }}>
                  Charged after 7-day free trial:{" "}
                  <Box component="b" sx={{ color: "#111", fontWeight: 600, fontSize: "1.1em" }}>
                    ${formatNumberWithCommas(calculateTotalPrice())}
                  </Box>
                  <Typography variant="body2" sx={{ color: "#16a34a", mt: 1.5, fontSize: "12px" }}>
                    Cancel anytime • No charge if canceled during trial
                  </Typography>
                </Typography>
              </Box>  */}
              {/* {!!calculateAllInOnePricing().addons?.length &&
                  <Typography variant="body2" color="#6b7280" mt={1.5} fontSize="12px">
                    {isYearly
                      ? `Annual billing includes 2 months free + FREE premium add-ons worth $${calculateAllInOnePricing().addons?.reduce((acc, addon) => acc + addon.price, 0)}/month.`
                      : `Switch to annual billing to save + get FREE premium add-ons worth $${calculateAllInOnePricing().addons?.reduce((acc, addon) => acc + addon.price, 0)}/month.`}
                  </Typography>
                } */}
            </Box>

            {/* Right side - Order Summary Section */}
            <Box
              sx={{
                width: { xs: "100%", md: "55%" },
                bgcolor: "#ffffff",
                p: { xs: 3, sm: 4, md: 6 },
                pb: { xs: 3, sm: 4, md: 6 },
                display: "flex",
                flexDirection: "column",
                border: { md: "1px solid #736E6E61", xs: "none" },
                borderRadius: { md: "12px", xs: 0 },
                // justifyContent: "center",
                minHeight: { xs: "500px", md: "auto" },
              }}
            >
              <Box sx={{ textAlign: "left", mb: 3, display: { xs: "none", md: "block" } }}>
                <Typography
                  color="#333333"
                  fontWeight="700"
                  fontSize={{ xs: "24px", lg: "28px" }}
                  mb="8px"
                  lineHeight="1.2"
                >
                  Start Your Full-Access Free Trial
                </Typography>
                <Box
                  sx={{
                    color: "#374151",
                    fontSize: "15px",
                    display: "inline-block",
                  }}
                >
                  After 7-Day Free Trial: {isYearly ? <Box component="span" sx={{ textDecoration: "line-through", fontSize: "16px", color: "#9ca3af" }}>
                    ${formatNumberWithCommas(calculateAllInOnePricing().annualMaxPrice)}
                  </Box> : ""} <b>${formatNumberWithCommas(isYearly ? calculateAllInOnePricing().annualPlanPerMonth : calculateTotalPrice())}/month {isYearly ? "(billed annually)" : ""}</b>, cancel anytime
                </Box>
              </Box>

              <Box
                sx={{ borderBottom: "1px solid #e5e7eb", mb: 3 }}
              />

              {!isTrial ? (
                <>
                  <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    mb={2}
                  >
                    <Grid size={{ xs: 6, sm: 6 }}>
                      <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                        First name
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="John"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        error={!!formErrors.firstName}
                        helperText={formErrors.firstName}
                        required
                        inputProps={{ maxLength: 50 }}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6 }}>
                      <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                        Last name
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="Doe"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        error={!!formErrors.lastName}
                        helperText={formErrors.lastName}
                        inputProps={{ maxLength: 50 }}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                        Work email
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="john@company.com"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        disabled={true}
                        required
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                        Phone number
                      </Typography>
                      <MuiPhone
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        fullWidth
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                        Create password
                      </Typography>
                      <TextField
                        variant="outlined"
                        type="password"
                        placeholder="Create a strong password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        error={!!formErrors.password}
                        helperText={formErrors.password}
                        disabled={isAutoRegistering}
                        required
                        autoComplete="new-password"
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                        fullWidth
                      />
                      <PasswordStrengthIndicator password={formData.password} transparent />
                    </Grid>
                  </Grid>

                  <Box sx={{ borderBottom: "1px solid #e5e7eb", mt: { xs: 0.5, sm: 1 }, mb: { xs: 1, sm: 2 } }} />

                  <Box width="100%" mb={2}>
                    <Typography fontSize="13px" mb={0.5} fontWeight={600} color="#374151">
                      Card Details
                    </Typography>
                    <Box
                      width="100%"
                      sx={{
                        position: "relative",
                        "& .CbHosted": {
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          padding: "14px 16px",
                          "&:hover, &--focus": {
                            borderColor: "#666666",
                          },
                        },
                      }}
                    >
                      <CardComponent
                        ref={cardRef}
                        currency="USD"
                        showTestCards={true}
                        styles={{
                          border: "1px solid #e5e7eb",
                          base: {
                            padding: "14px 16px",
                            border: "1px solid #e5e7eb",
                            fontSize: "14px",
                            fontFamily: "Inter, sans-serif",
                            color: "#333333",
                            "::placeholder": {
                              color: "#9ca3af",
                            },
                          },
                          invalid: {
                            color: "#ff0000",
                          },
                        }}
                        onChange={(status: any) => {
                          console.log("Card status:", status);
                          if (status.error) {
                            setCardError(status.error.message);
                            setIsCardValid(false);
                          } else if (!status.complete) {
                            setCardError(null);
                            setIsCardValid(false);
                          } else {
                            setCardError(null);
                            setIsCardValid(true);
                          }
                        }}
                        onReady={() => {
                          console.log("Card component ready");
                          setIsCardReady(true);
                          setIsCardValid(false);
                        }}
                        onBlur={() => {
                          console.log("Card component blurred");
                        }}
                        onFocus={() => {
                          console.log("Card component focused");
                        }}
                      />
                    </Box>
                    {cardError && (
                      <Typography color="error" fontSize="12px" mt={1}>
                        {cardError}
                      </Typography>
                    )}
                  </Box>

                  <Box display="flex" justifyContent="end" flexDirection="column">
                    {!showPromoField ? (
                      <>
                        {appliedPromo ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "4px 8px",
                              backgroundColor: "#F0FFF4",
                              borderRadius: "4px",
                              width: "fit-content",
                              marginLeft: "auto",
                            }}
                          >
                            <Typography color="#1D9652" fontSize="14px">
                              {appliedPromo.code} applied
                            </Typography>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setAppliedPromo(null);
                                setPromoCode("");
                              }}
                            >
                              <path
                                d="M8 1C4.1 1 1 4.1 1 8C1 11.9 4.1 15 8 15C11.9 15 15 11.9 15 8C15 4.1 11.9 1 8 1ZM10.7 11.5L8 8.8L5.3 11.5L4.5 10.7L7.2 8L4.5 5.3L5.3 4.5L8 7.2L10.7 4.5L11.5 5.3L8.8 8L11.5 10.7L10.7 11.5Z"
                                fill="#1D9652"
                              />
                            </svg>
                          </Box>
                        ) : null}
                      </>
                    ) : (
                      <TextField
                        variant="outlined"
                        placeholder="Promo Code:"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          setPromoError("");
                        }}
                        error={!!promoError}
                        helperText={promoError}
                        sx={(theme) => ({
                          width: "100%",
                          marginBottom: "16px",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e5e7eb",
                          },
                          [theme.breakpoints.down("sm")]: {
                            "& .MuiOutlinedInput-input": {
                              height: "1em",
                              padding: "14px 14px",
                            },
                          },
                        })}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end" sx={{ paddingRight: "10px" }}>
                                <Box display="flex" justifyContent="center" alignItems="center">
                                  <Box
                                    sx={{
                                      background: theme.palette.primary.main,
                                      borderRadius: "4px",
                                      padding: "4px 10px",
                                      marginRight: "4px",
                                      minWidth: "57px",
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      color: "#fff",
                                      cursor: "pointer",
                                    }}
                                    onClick={validateCoupon}
                                  >
                                    Apply
                                  </Box>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="16"
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setShowPromoField(false);
                                      setPromoError("");
                                    }}
                                  >
                                    <path
                                      d="M8.07324 1C4.17324 1 1.07324 4.1 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z"
                                      fill="#A3A3A3"
                                    />
                                  </svg>
                                </Box>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </Box>
                </>
              ) : null}

              <Button
                variant="contained"
                onClick={handleGetStarted}
                disabled={isLoading || isAutoRegistering || (!isTrial && !isCardValid)}
                disableElevation
                fullWidth
                sx={{
                  mt: { xs: 2.5, sm: 3 },
                  py: { xs: 1.2, sm: 1.6 },
                  borderRadius: "8px",
                  // backgroundColor: "#000",
                  color: "#fff !important",
                  fontWeight: 600,
                  fontSize: { xs: "14px", sm: "16px" },
                  textTransform: "none",
                  // "&:hover": { backgroundColor: "#333 !important" },
                  "&.Mui-disabled": {
                    bgcolor: "#cccccc !important",
                    color: "#666666 !important",
                  },
                }}
              >
                {isLoading || isAutoRegistering ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : !isTrial && !isCardValid ? (
                  "Please complete card details"
                ) : (
                  "Start My Free Trial"
                )}
              </Button>
              <Typography fontSize="14px" color="#9ca3af" textAlign="center" lineHeight={1.6} mt={2}>
                By starting your {noTrial ? "plan" : "trial"}, you agree to our{" "}
                <a
                  href="https://easywebinar.com/terms-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", cursor: "pointer", color: "#667EEA" }}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://easywebinar.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", cursor: "pointer", color: "#667EEA" }}
                >
                  Privacy Policy
                </a>
              </Typography>

              {/* <Typography fontSize="12px" color="#6b7280" textAlign="center" mt={2} mb={0.5}>
                100% Free Trial • Cancel Anytime • No Hidden Fees
              </Typography>


              <Typography fontSize="11px" color="#9ca3af" textAlign="center" lineHeight={1.6}>
                By starting your {noTrial ? "plan" : "trial"}, you agree to our{" "}
                <a
                  href="https://easywebinar.com/terms-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", cursor: "pointer", color: "#667EEA" }}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://easywebinar.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", cursor: "pointer", color: "#667EEA" }}
                >
                  Privacy Policy
                </a>
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 2.75 },
                  mt: 2,
                }}
              >
                <Box
                  component="img"
                  src={`/images/batches/batch1.webp`}
                  alt=""
                  sx={{ maxWidth: { xs: 45, sm: 60 }, maxHeight: { xs: 45, sm: 60 }, display: "block", flexShrink: 0 }}
                />
                <Box sx={{ borderRight: "1px solid #C5C5C5", pr: 2.5 }}>
                  <Box
                    component="img"
                    src={`/images/batches/batch2.webp`}
                    alt=""
                    sx={{ maxWidth: { xs: 45, sm: 68 }, maxHeight: { xs: 45, sm: 68 }, mt: { xs: "-4px", sm: "-8px" }, display: "block", flexShrink: 0 }}
                  />
                </Box>
                <Box
                  component="img"
                  src={`/images/batches/batch3.webp`}
                  alt=""
                  sx={{ maxWidth: { xs: 45, sm: 60 }, maxHeight: { xs: 45, sm: 60 }, display: "block", flexShrink: 0 }}
                />
                <Box
                  component="img"
                  src={`/images/batches/batch4.webp`}
                  alt=""
                  sx={{ maxWidth: { xs: 45, sm: 60 }, maxHeight: { xs: 45, sm: 60 }, display: "block", flexShrink: 0 }}
                />

                <Box
                  component="img"
                  src={`/images/batches/batch5.webp`}
                  alt=""
                  sx={{ maxWidth: { xs: 45, sm: 60 }, maxHeight: { xs: 45, sm: 60 }, display: "block", flexShrink: 0 }}
                />

              </Box> */}
            </Box>
            
          </Box>
        </Box>
      </Box>
      <AccountVerification
        onClose={handleVerificationDialogClose}
        open={verificationDialogOpen}
        onProceed={handleVerificationDialogProceed}
        phoneNumber=""
        email={formData.email}
        password={formData.password}
        companyId={verificationData.companyId}
        companyName={verificationData.companyName}
        infLeadSquaredId={verificationData.infLeadSquaredId}
      />
    </>
  );
};
export default MainSignup;
